import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { Color } from 'three';
import { DxfViewer } from 'dxf-viewer';
import {
  CAD_FONT_URLS,
  CadDrawing,
  CadDxfError,
  CadViewerService,
} from 'src/app/shared/services/cad-viewer.service';

export type CadViewerCanvasState = 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';

interface CadLayer {
  name: string;
  displayName: string;
  color: string;
  visible: boolean;
}

const BACKGROUNDS = { dark: '#1f2329', light: '#ffffff' } as const;
type Background = keyof typeof BACKGROUNDS;
const BACKGROUND_STORAGE_KEY = 'cadViewerBackground';

const PHASE_LABELS: Record<string, string> = {
  convert: 'Converting DWG on the server',
  font: 'Loading fonts',
  fetch: 'Downloading drawing',
  parse: 'Parsing drawing',
  prepare: 'Preparing view',
};

function createDxfWorker(): Worker {
  return new Worker(new URL('../dxf-viewer.worker', import.meta.url), { type: 'module' });
}

@Component({
  selector: 'app-cad-viewer-canvas',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cad-viewer-canvas.component.html',
  styleUrls: ['./cad-viewer-canvas.component.css'],
})
export class CadViewerCanvasComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() drawing: CadDrawing | null = null;
  /** Replaces the plain `file_url` link when the original needs an authenticated download. */
  @Input() downloadHandler: (() => void) | null = null;

  @Output() loaded = new EventEmitter<void>();
  @Output() loadError = new EventEmitter<string>();

  @ViewChild('viewport', { static: true }) viewportRef!: ElementRef<HTMLDivElement>;
  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLDivElement>;

  state: CadViewerCanvasState = 'idle';
  message = '';
  progressLabel = '';
  progressPct: number | null = null;
  layers: CadLayer[] = [];
  layersOpen = false;
  background: Background = localStorage.getItem(BACKGROUND_STORAGE_KEY) === 'light' ? 'light' : 'dark';
  isFullscreen = false;
  readonly fullscreenSupported = typeof document !== 'undefined' && !!document.fullscreenEnabled;

  private viewer: DxfViewer | null = null;
  private progressPhase = '';
  private objectUrl: string | null = null;
  private fetchSub: Subscription | null = null;
  private requestSeq = 0;
  private loadQueue: Promise<unknown> = Promise.resolve();
  private refitOnResize = false;
  private refitTimer: ReturnType<typeof setTimeout> | undefined;
  private viewReady = false;

  constructor(
    private cadViewer: CadViewerService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.drawing) this.open(this.drawing);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady || !changes['drawing']) return;
    const prev = changes['drawing'].previousValue as CadDrawing | null;
    if (this.drawing && this.drawing.id !== prev?.id) this.open(this.drawing);
    if (!this.drawing) this.reset();
  }

  ngOnDestroy(): void {
    if (document.fullscreenElement === this.hostRef.nativeElement) {
      document.exitFullscreen().catch(() => {});
    }
    clearTimeout(this.refitTimer);
    this.requestSeq++;
    this.fetchSub?.unsubscribe();
    this.viewer?.Destroy();
    this.viewer = null;
    this.revokeObjectUrl();
  }

  retry(): void {
    if (this.drawing) this.open(this.drawing);
  }

  fitView(): void {
    const viewer = this.viewer;
    const bounds = viewer?.GetBounds();
    if (!viewer || !bounds) return;
    const origin = viewer.GetOrigin();
    viewer.FitView(
      bounds.minX - origin.x,
      bounds.maxX - origin.x,
      bounds.minY - origin.y,
      bounds.maxY - origin.y,
      0.05
    );
  }

  toggleLayer(layer: CadLayer): void {
    layer.visible = !layer.visible;
    this.viewer?.ShowLayer(layer.name, layer.visible);
  }

  setAllLayers(visible: boolean): void {
    for (const layer of this.layers) {
      layer.visible = visible;
      this.viewer?.ShowLayer(layer.name, visible);
    }
  }

  toggleFullscreen(): void {
    if (!this.fullscreenSupported) return;
    const host = this.hostRef.nativeElement;
    if (document.fullscreenElement === host) {
      document.exitFullscreen().catch(() => {});
    } else {
      host.requestFullscreen().catch(() => {});
    }
  }

  @HostListener('document:fullscreenchange')
  onFullscreenChange(): void {
    const host = this.hostRef.nativeElement;
    const isFullscreen = document.fullscreenElement === host;
    if (isFullscreen === this.isFullscreen) return;
    this.isFullscreen = isFullscreen;
    if (isFullscreen) host.focus();
    // The (animated, on macOS) transition resizes the canvas in several steps around this
    // event, so keep re-fitting on every dxf-viewer "resized" until it settles.
    this.fitView();
    this.refitOnResize = true;
    clearTimeout(this.refitTimer);
    this.refitTimer = setTimeout(() => {
      this.refitOnResize = false;
      this.fitView();
    }, 1500);
    this.cdr.markForCheck();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'f' && event.key !== 'F') return;
    if (event.ctrlKey || event.metaKey || event.altKey || this.state !== 'ready') return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    event.preventDefault();
    this.toggleFullscreen();
  }

  toggleBackground(): void {
    this.background = this.background === 'dark' ? 'light' : 'dark';
    localStorage.setItem(BACKGROUND_STORAGE_KEY, this.background);
    this.viewer?.SetClearColor(BACKGROUNDS[this.background]);
  }

  private ensureViewer(): DxfViewer {
    if (!this.viewer) {
      this.viewer = this.zone.runOutsideAngular(
        () =>
          new DxfViewer(this.viewportRef.nativeElement, {
            autoResize: true,
            colorCorrection: true,
            antialias: true,
            clearColor: new Color(BACKGROUNDS[this.background]),
          })
      );
      this.viewer.Subscribe('resized', () => {
        if (this.refitOnResize) this.fitView();
      });
    }
    return this.viewer;
  }

  private open(drawing: CadDrawing): void {
    const seq = ++this.requestSeq;
    this.fetchSub?.unsubscribe();
    this.layers = [];
    this.layersOpen = false;
    const needsConversion = drawing.format === 'dwg' && drawing.conversion_status !== 'ready';
    this.setProgress(needsConversion ? 'convert' : 'fetch', null);
    this.state = 'loading';
    this.message = '';
    this.cdr.markForCheck();

    this.fetchSub = this.cadViewer.fetchDxf(drawing).subscribe({
      next: (url) => {
        if (seq !== this.requestSeq) {
          URL.revokeObjectURL(url);
          return;
        }
        this.revokeObjectUrl();
        this.objectUrl = url;
        this.render(url, seq);
      },
      error: (err: unknown) => {
        if (seq !== this.requestSeq) return;
        const unavailable = err instanceof CadDxfError && err.code === 'CONVERTER_UNAVAILABLE';
        this.fail(unavailable ? 'unavailable' : 'error', (err as Error)?.message || 'The drawing could not be loaded.');
      },
    });
  }

  private render(url: string, seq: number): void {
    let viewer: DxfViewer;
    try {
      viewer = this.ensureViewer();
    } catch {
      this.fail('error', 'This browser cannot display drawings (WebGL is not available).');
      return;
    }

    let lastPct = -1;
    // DxfViewer.Load is not re-entrant: overlapping calls merge both scenes, so loads are chained.
    const load = this.loadQueue.then(() => {
      if (seq !== this.requestSeq) return false;
      return this.zone
        .runOutsideAngular(() =>
          viewer.Load({
            url,
            fonts: CAD_FONT_URLS,
            workerFactory: createDxfWorker,
            progressCbk: (phase, processed, total) => {
              const pct = total ? Math.floor((processed / total) * 100) : null;
              if (pct === lastPct && phase === this.progressPhase) return;
              lastPct = pct ?? -1;
              this.zone.run(() => {
                if (seq !== this.requestSeq) return;
                this.setProgress(phase, pct);
                this.cdr.markForCheck();
              });
            },
          })
        )
        .then(() => true);
    });
    this.loadQueue = load.catch(() => undefined);

    load
      .then((didLoad) => {
        if (seq !== this.requestSeq) {
          if (didLoad && this.state !== 'loading' && this.state !== 'ready') viewer.Clear();
          return;
        }
        this.layers = Array.from(viewer.GetLayers(true))
          .map((l) => ({
            name: l.name,
            displayName: l.displayName || l.name,
            color: `#${(l.color ?? 0xffffff).toString(16).padStart(6, '0')}`,
            visible: true,
          }))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
        this.state = 'ready';
        this.cdr.markForCheck();
        this.loaded.emit();
      })
      .catch((err: Error) => {
        if (seq !== this.requestSeq) return;
        this.fail('error', `The drawing could not be displayed: ${err?.message || 'invalid DXF'}`);
      });
  }

  private setProgress(phase: string, pct: number | null): void {
    this.progressPhase = phase;
    this.progressLabel = PHASE_LABELS[phase] || 'Loading';
    this.progressPct = pct;
  }

  private fail(state: 'unavailable' | 'error', message: string): void {
    this.state = state;
    this.message = message;
    this.viewer?.Clear();
    this.cdr.markForCheck();
    this.loadError.emit(message);
  }

  private reset(): void {
    this.requestSeq++;
    this.fetchSub?.unsubscribe();
    this.viewer?.Clear();
    this.revokeObjectUrl();
    this.layers = [];
    this.state = 'idle';
    this.cdr.markForCheck();
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
  }
}
